"use client";
import { TextAdRow } from "@/components/ads/positions";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useParams } from "next/navigation";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Globe,
  Menu,
  Search,
  User,
  X,
} from "lucide-react";
import { Link, usePathname, useRouter } from "@/i18n/routing";
import { ROUTES } from "@/lib/routes";
import {
  EJHEALTH_COLORS,
  EJHEALTH_FONT,
  EJHEALTH_TYPE,
} from "@/lib/theme/ej-health";
import { NAV_ITEMS, MORE_NAV_ITEMS } from "@/fixtures/ej-health/fixtures";
import { EjHealthSearchBar } from "./search/search-bar";

// Chinese-only language options — Traditional + Simplified.
const LOCALES: { value: "zh-TW" | "zh-CN"; label: string; short: string }[] = [
  { value: "zh-TW", label: "繁體中文", short: "繁體" },
  { value: "zh-CN", label: "简体中文", short: "简体" },
];

export function Nav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerMoreOpen, setDrawerMoreOpen] = useState(false);

  const params = useParams<{ locale?: string }>();
  const pathname = usePathname();
  const router = useRouter();
  const currentLocale = (params?.locale ?? "zh-TW") as string;

  /**
   * A section is active when you are ON it — nothing else.
   *
   * It used to be a `useState` seeded with 「醫健新知」 and written on click, so
   * 醫健新知 was underlined on the home page, on an article, on every page the
   * reader had not clicked their way to; and once clicked, an item stayed
   * underlined after navigating elsewhere by any other route. `usePathname`
   * from `@/i18n/routing` returns the path WITHOUT the locale prefix, which is
   * exactly the shape the fixture hrefs are written in, so this is an equality
   * test and not a prefix match: `/ej-health/eat-well` must not light up
   * 醫健新知, and the home page lights up nothing.
   */
  const isActive = (href: string) =>
    decodeURIComponent(pathname) === decodeURIComponent(href);

  const chooseLocale = (locale: "zh-TW" | "zh-CN") => {
    if (locale === currentLocale) return;
    router.replace(pathname, { locale });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <header
      className="sticky top-0 z-50 w-full border-b lg:border-b-0"
      style={{
        backgroundColor: EJHEALTH_COLORS.surface,
        borderColor: EJHEALTH_COLORS.divider,
        boxShadow: scrolled ? "0 6px 18px -14px rgba(26,31,34,0.35)" : "none",
        transition: "box-shadow .3s ease",
        fontFamily: EJHEALTH_FONT.body,
        color: EJHEALTH_COLORS.dark,
      }}
    >
      {/* Row 1 — Back to HKEJ utility bar */}
      <div
        style={{
          backgroundColor: EJHEALTH_COLORS.surfaceAlt,
          borderBottom: `1px solid ${EJHEALTH_COLORS.divider}`,
        }}
      >
        <div className="mx-auto w-full max-w-[1280px] px-6 lg:px-10">
          <div className="flex h-9 items-center">
            <Link
              href="/"
              aria-label="回到信報財經新聞"
              className="group inline-flex items-center gap-1.5 transition-colors"
              style={{ color: EJHEALTH_COLORS.textMuted }}
            >
              <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" />
              <Image
                src="/images/nav-v2/hkej-back.png"
                alt="信報 www.hkej.com"
                width={133}
                height={23}
                className="h-[18px] w-auto object-contain"
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Row 2 — Logo row */}
      <div style={{ backgroundColor: EJHEALTH_COLORS.surface }}>
        <div className="mx-auto w-full max-w-[1280px] px-6 lg:px-10">
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4 py-5">
            <button
              type="button"
              className="inline-flex h-10 items-center justify-center lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="開啟選單"
              style={{ color: EJHEALTH_COLORS.dark }}
            >
              <Menu className="h-6 w-6" />
            </button>

            <Link
              href="/ej-health"
              className="flex items-center gap-3 lg:justify-self-start"
              aria-label="信健康"
            >
              <Image
                src="/images/ej-health/ej-health-logo.png"
                alt="信健康"
                width={165}
                height={60}
                className="h-12 w-auto object-contain lg:h-[52px]"
                priority
              />
            </Link>

            <div className="flex items-center gap-4 md:gap-6 justify-self-end">
              <button
                type="button"
                aria-label="搜尋"
                aria-expanded={searchOpen}
                onClick={() => setSearchOpen((v) => !v)}
                className="inline-flex h-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
                style={{ color: EJHEALTH_COLORS.dark }}
              >
                {searchOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Search className="h-5 w-5" />
                )}
              </button>

              {/* Functional locale switch — tablet + desktop. Mobile uses the drawer toggle. */}
              <LocaleMenu
                currentLocale={currentLocale}
                onChoose={chooseLocale}
              />

              {/* Was a `<button>` with no handler — the masthead's only dead
                  control. ejinsight and ej-monthly already point theirs at the
                  account page. */}
              <Link
                href={ROUTES.profile}
                aria-label="帳戶"
                className="hidden sm:inline-flex h-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
                style={{ color: EJHEALTH_COLORS.dark }}
              >
                <User className="h-5 w-5" />
              </Link>
              <Link
                href="/subscription"
                className="hidden sm:inline-flex items-center px-5 py-2.5 transition-colors"
                style={{
                  ...EJHEALTH_TYPE.button,
                  backgroundColor: EJHEALTH_COLORS.accent,
                  color: EJHEALTH_COLORS.dark,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    EJHEALTH_COLORS.accentDark;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    EJHEALTH_COLORS.accent;
                }}
              >
                訂閱
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Expanding search bar — desktop + tablet + mobile */}
      <div
        // `overflow-hidden` only while closed: the suggestion list drops out of
        // this container, and clipping it left the box open with nothing under
        // it.
        className={searchOpen ? "border-t" : "overflow-hidden border-t"}
        style={{
          borderColor: searchOpen ? EJHEALTH_COLORS.divider : "transparent",
          maxHeight: searchOpen ? 160 : 0,
          opacity: searchOpen ? 1 : 0,
          transition:
            "max-height .3s ease, opacity .25s ease, border-color .3s ease",
          backgroundColor: EJHEALTH_COLORS.surfaceAlt,
        }}
      >
        <div className="mx-auto w-full max-w-[1280px] px-6 lg:px-10">
          {/* Type-ahead, and the vertical's OWN result page.
              It was a bare input that pushed to the MAIN site's `/search?q=`,
              so a reader searching 信健康 got HKEJ's results — and there were
              no suggestions at all. QA: "should have auto suggestion when
              inputting text" and "missing search result page". */}
          <div className="py-4">
            <EjHealthSearchBar
              // Remounted on each open, so the field starts empty — QA:
              // "should clear the text in search bar after closing the search
              // bar". The text used to survive a close/reopen, so the box came
              // back holding a query the reader had already abandoned.
              key={searchOpen ? "open" : "closed"}
              autoFocus={searchOpen}
              onNavigate={() => {
                setSearchOpen(false);
                setMobileOpen(false);
              }}
            />
            {/* Search text ads under the toggled search bar (SponsorBanner1–5); only mounted while open. */}
            {searchOpen ? <TextAdRow terms={[null, null, null, null, null]} startIndex={1} className="pt-2" /> : null}
          </div>
        </div>
      </div>

      {/* Row 3 — Main nav (desktop only) */}
      <div
        className="hidden lg:block"
        style={{
          backgroundColor: EJHEALTH_COLORS.surface,
          borderTop: `1px solid ${EJHEALTH_COLORS.divider}`,
        }}
      >
        <div className="mx-auto w-full max-w-[1280px] px-6 lg:px-10">
          <nav className="flex h-12 items-center gap-6">
            {NAV_ITEMS.map((item) => {
              if (item.hasChevron) {
                return (
                  <MoreMenu
                    key={item.label}
                    label={item.label}
                    items={MORE_NAV_ITEMS}
                  />
                );
              }
              const active = isActive(item.href);
              return (
                <Link
                  key={item.label}
                  href={item.href}
                  className="group inline-flex items-center gap-1 whitespace-nowrap py-2 transition-colors"
                  style={{
                    ...EJHEALTH_TYPE.navLink,
                    color: active
                      ? EJHEALTH_COLORS.primary
                      : EJHEALTH_COLORS.dark,
                    borderBottom: active
                      ? `2px solid ${EJHEALTH_COLORS.accent}`
                      : "2px solid transparent",
                    marginBottom: -2,
                  }}
                  aria-current={active ? "page" : undefined}
                  onMouseEnter={(e) => {
                    if (!active)
                      e.currentTarget.style.color = EJHEALTH_COLORS.primary;
                  }}
                  onMouseLeave={(e) => {
                    if (!active)
                      e.currentTarget.style.color = EJHEALTH_COLORS.dark;
                  }}
                >
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Mobile + tablet drawer — slides in from the LEFT. Kept mounted so it
          animates both in and out; clicks pass through when closed. */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden ${
          mobileOpen ? "" : "pointer-events-none"
        }`}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          aria-label="關閉選單"
          className="absolute inset-0 transition-opacity duration-300 ease-out"
          style={{
            backgroundColor: "rgba(26,31,34,0.45)",
            opacity: mobileOpen ? 1 : 0,
          }}
          onClick={() => setMobileOpen(false)}
        />
        <aside
          className="absolute left-0 top-0 flex h-full w-[88%] max-w-[380px] flex-col"
          style={{
            backgroundColor: EJHEALTH_COLORS.surface,
            color: EJHEALTH_COLORS.dark,
            transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
            transition: "transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: mobileOpen ? "0 0 48px rgba(26,31,34,0.28)" : "none",
          }}
        >
          <div
            className="flex items-center justify-between px-6 py-5"
            style={{ borderBottom: `1px solid ${EJHEALTH_COLORS.divider}` }}
          >
            <Image
              src="/images/ej-health/ej-health-logo.png"
              alt="信健康"
              width={165}
              height={60}
              className="h-10 w-auto object-contain"
            />
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="關閉選單"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
              style={{ color: EJHEALTH_COLORS.dark }}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Drawer search */}
          <div className="px-6 pt-5">
            <EjHealthSearchBar
              placeholder="搜尋"
              onNavigate={() => setMobileOpen(false)}
            />
          </div>

          <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-6 py-5">
            {NAV_ITEMS.map((item) => {
              if (item.hasChevron) {
                return (
                  <div key={item.label}>
                    <button
                      type="button"
                      aria-expanded={drawerMoreOpen}
                      onClick={() => setDrawerMoreOpen((v) => !v)}
                      className="flex w-full items-center justify-between border-b py-4 transition-colors"
                      style={{
                        ...EJHEALTH_TYPE.navLink,
                        color: drawerMoreOpen
                          ? EJHEALTH_COLORS.primary
                          : EJHEALTH_COLORS.dark,
                        borderColor: EJHEALTH_COLORS.divider,
                      }}
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        className="h-4 w-4 transition-transform duration-200"
                        style={{
                          transform: drawerMoreOpen ? "rotate(180deg)" : "none",
                        }}
                      />
                    </button>
                    <div
                      className="overflow-hidden"
                      style={{
                        maxHeight: drawerMoreOpen
                          ? MORE_NAV_ITEMS.length * 52
                          : 0,
                        opacity: drawerMoreOpen ? 1 : 0,
                        transition:
                          "max-height .3s ease, opacity .25s ease",
                      }}
                    >
                      {MORE_NAV_ITEMS.map((sub) => (
                        <Link
                          key={sub.label}
                          href={sub.href}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center border-b py-3 pl-4 transition-colors"
                          style={{
                            ...EJHEALTH_TYPE.navLink,
                            fontSize: 13,
                            color: EJHEALTH_COLORS.textMuted,
                            borderColor: EJHEALTH_COLORS.divider,
                          }}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={item.label}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between border-b py-4 transition-colors"
                  // The drawer marks the current section too, on the same rule
                  // as the desktop row: on it, or not at all.
                  aria-current={isActive(item.href) ? "page" : undefined}
                  style={{
                    ...EJHEALTH_TYPE.navLink,
                    color: isActive(item.href)
                      ? EJHEALTH_COLORS.primary
                      : EJHEALTH_COLORS.dark,
                    borderColor: EJHEALTH_COLORS.divider,
                  }}
                >
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Language toggle + subscribe */}
          <div
            className="flex flex-col gap-4 px-6 py-5"
            style={{ borderTop: `1px solid ${EJHEALTH_COLORS.divider}` }}
          >
            <div className="flex items-center gap-3">
              <Globe
                className="h-4 w-4"
                style={{ color: EJHEALTH_COLORS.textMuted }}
              />
              {LOCALES.map((loc, idx) => {
                const active = loc.value === currentLocale;
                return (
                  <button
                    key={loc.value}
                    type="button"
                    onClick={() => {
                      chooseLocale(loc.value);
                      setMobileOpen(false);
                    }}
                    style={{
                      ...EJHEALTH_TYPE.navLink,
                      letterSpacing: "0.06em",
                      color: active
                        ? EJHEALTH_COLORS.primary
                        : EJHEALTH_COLORS.textMuted,
                      borderRight:
                        idx < LOCALES.length - 1
                          ? `1px solid ${EJHEALTH_COLORS.divider}`
                          : undefined,
                      paddingRight: idx < LOCALES.length - 1 ? 12 : 0,
                    }}
                  >
                    {loc.label}
                  </button>
                );
              })}
            </div>
            <Link
              href="/subscription"
              onClick={() => setMobileOpen(false)}
              className="inline-flex items-center justify-center px-4 py-3"
              style={{
                ...EJHEALTH_TYPE.button,
                backgroundColor: EJHEALTH_COLORS.accent,
                color: EJHEALTH_COLORS.dark,
              }}
            >
              訂閱
            </Link>
          </div>
        </aside>
      </div>
    </header>
  );
}

/* ────────────────────────────────────────────────────────
   Locale dropdown — functional language switch (tablet + desktop)
   ──────────────────────────────────────────────────────── */
function LocaleMenu({
  currentLocale,
  onChoose,
}: {
  currentLocale: string;
  onChoose: (locale: "zh-TW" | "zh-CN") => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      )
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative hidden sm:inline-flex">
      <button
        type="button"
        aria-label="切換語言"
        aria-expanded={open}
        title="繁體中文 / 简体中文"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
        style={{ color: EJHEALTH_COLORS.dark }}
      >
        <Globe className="h-5 w-5" />
      </button>
      {open && (
        <ul
          role="menu"
          aria-label="切換語言"
          className="absolute right-0 z-[70]"
          style={{
            top: "calc(100% + 8px)",
            minWidth: 168,
            backgroundColor: EJHEALTH_COLORS.surface,
            border: `1px solid ${EJHEALTH_COLORS.divider}`,
            borderRadius: 12,
            boxShadow: "0 16px 32px rgba(26,31,34,0.14)",
            padding: 4,
          }}
        >
          {LOCALES.map((loc) => {
            const active = loc.value === currentLocale;
            return (
              <li key={loc.value}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => {
                    onChoose(loc.value);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between rounded-lg transition-colors"
                  style={{
                    ...EJHEALTH_TYPE.navLink,
                    fontSize: 13,
                    padding: "8px 12px",
                    color: active
                      ? EJHEALTH_COLORS.primary
                      : EJHEALTH_COLORS.dark,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      EJHEALTH_COLORS.surfaceAlt;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <span>{loc.label}</span>
                  {active && <Check className="h-4 w-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   "更多" dropdown — extra sections surfaced from the desktop nav
   ──────────────────────────────────────────────────────── */
function MoreMenu({
  label,
  items,
}: {
  label: string;
  items: { label: string; href: string }[];
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      )
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="group inline-flex items-center gap-1 whitespace-nowrap py-2 transition-colors"
        style={{
          ...EJHEALTH_TYPE.navLink,
          color: open ? EJHEALTH_COLORS.primary : EJHEALTH_COLORS.dark,
          borderBottom: open
            ? `2px solid ${EJHEALTH_COLORS.accent}`
            : "2px solid transparent",
          marginBottom: -2,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = EJHEALTH_COLORS.primary;
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.color = EJHEALTH_COLORS.dark;
        }}
      >
        <span>{label}</span>
        <ChevronDown
          className="h-3.5 w-3.5 transition-transform duration-200"
          style={{ transform: open ? "rotate(180deg)" : "none" }}
        />
      </button>
      {open && (
        <ul
          role="menu"
          aria-label={label}
          className="absolute right-0 z-[70]"
          style={{
            top: "calc(100% + 8px)",
            minWidth: 180,
            backgroundColor: EJHEALTH_COLORS.surface,
            border: `1px solid ${EJHEALTH_COLORS.divider}`,
            borderRadius: 12,
            boxShadow: "0 16px 32px rgba(26,31,34,0.14)",
            padding: 4,
          }}
        >
          {items.map((item) => (
            <li key={item.label} role="none">
              <Link
                href={item.href}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex w-full items-center rounded-lg transition-colors"
                style={{
                  ...EJHEALTH_TYPE.navLink,
                  fontSize: 13,
                  padding: "9px 12px",
                  color: EJHEALTH_COLORS.dark,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    EJHEALTH_COLORS.surfaceAlt;
                  e.currentTarget.style.color = EJHEALTH_COLORS.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = EJHEALTH_COLORS.dark;
                }}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
